/**
 * Fase 5 (pedido de 19/10, item 7) — Mensagens automáticas editáveis por unidade.
 *
 * Contrato com o backend (nomes exatos):
 * - GET  /master/mensagens?unit_id=<id|vazio> -> {itens: [{chave, rotulo, canal ("sms"|"whatsapp"|"wame"),
 *   gatilho, variaveis: [...], editavel, texto_padrao, texto_global|null, texto_unidade|null,
 *   texto_efetivo, max_caracteres|null}]}. unit_id vazio = padrão do sistema (global).
 * - PUT  /master/mensagens {unit_id|null, chave, canal, texto} — texto vazio remove a personalização
 *   daquele nível; variável desconhecida = 400 com a lista.
 * - POST /master/mensagens/preview {chave, canal, unit_id|null, texto?} -> {texto_renderizado,
 *   caracteres, sms_partes|null, avisos: [...]}.
 * Resolução no envio: texto da unidade > texto global editado > padrão do código.
 * Campos a mais que o backend manda (usados quando vêm): variaveis_detalhe [{nome, descricao,
 * exemplo}], obrigatorias ["{codigo}" | "{a}, {b}" = uma delas], limite_texto (1000), origem_efetivo,
 * observacao, oficial {template, observacao} (WhatsApp) e, no preview, texto_sms. O PUT recusa (400):
 * emojis, mais de limite_texto, variável desconhecida, obrigatória faltando e mensagem fixa.
 *
 * O backend é a fonte da verdade (render e validação); aqui só o que a TELA precisa: agrupar por
 * canal, de onde vem o texto, variáveis do texto, tamanho do SMS e avisos imediatos de digitação.
 */

/** Ordem e nomes dos canais na tela. */
export const CANAIS = [
  {
    id: "sms",
    titulo: "SMS",
    descricao: "Enviados pela Allcance. Saem sem acentos e com no máximo 160 caracteres (o que passar é cortado).",
  },
  {
    id: "whatsapp",
    titulo: "WhatsApp",
    descricao: "Valem para o envio pelo provedor antigo. No WhatsApp oficial sai o template aprovado pela Meta.",
  },
  {
    id: "wame",
    titulo: "Links do WhatsApp (wa.me)",
    descricao: "Texto que já vem escrito quando o link abre o WhatsApp de quem clicou. Não passa pela API.",
  },
];
export const CANAL_LBL = { sms: "SMS", whatsapp: "WhatsApp", wame: "Link wa.me" };

export const SMS_LIMITE = 160;

/** Origem do texto em uso no nível escolhido. */
export const ORIGEM_LBL = {
  unidade: "Desta unidade",
  global: "Padrão editado",
  padrao: "Padrão do sistema",
};

const VAR_RE = /\{([^{}]*)\}/g;
const tem = (v) => typeof v === "string" && v.trim() !== "";

/** Variável do catálogo -> {nome, descricao}. Aceita "nome", "{nome}" ou {nome|chave, descricao}. */
export function normalizaVariavel(v) {
  if (v && typeof v === "object") {
    const nome = String(v.nome ?? v.chave ?? v.var ?? v.id ?? "").replace(/[{}]/g, "").trim();
    return nome ? { nome, descricao: String(v.descricao ?? v.desc ?? v.rotulo ?? "") } : null;
  }
  const nome = String(v ?? "").replace(/[{}]/g, "").trim();
  return nome ? { nome, descricao: "" } : null;
}

/** Lista de variáveis permitidas do item (com descrição quando vem variaveis_detalhe), sem repetição. */
export function variaveisDoItem(item) {
  const vistos = new Set();
  const det = Array.isArray(item?.variaveis_detalhe) && item.variaveis_detalhe.length ? item.variaveis_detalhe : null;
  return (det || (Array.isArray(item?.variaveis) ? item.variaveis : []))
    .map(normalizaVariavel)
    .filter((v) => v && !vistos.has(v.nome) && vistos.add(v.nome));
}

/** Grupos de variáveis obrigatórias: ["{codigo}", "{a}, {b}"] -> [["codigo"], ["a", "b"]]. */
export function gruposObrigatorios(item) {
  return (Array.isArray(item?.obrigatorias) ? item.obrigatorias : [])
    .map((g) => (Array.isArray(g) ? g.map(String) : String(g ?? "").split(",")).map((v) => v.replace(/[{}\s]/g, "")).filter(Boolean))
    .filter((g) => g.length);
}

/** De onde vem o texto em uso: o backend manda origem_efetivo (considera texto salvo inválido). */
export function origemDoItem(item, unitId) {
  const o = item?.origem_efetivo;
  if (o === "unidade" && !unitId) return origemTexto(item, unitId);
  return ["unidade", "global", "padrao"].includes(o) ? o : origemTexto(item, unitId);
}

/** Nomes das variáveis usadas no texto ({nome} -> "nome"), sem repetição, na ordem. */
export function variaveisNoTexto(texto) {
  const out = [];
  for (const m of String(texto ?? "").matchAll(VAR_RE)) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
}

/** Variáveis do texto que não estão na lista permitida (ordenadas). */
export function variaveisDesconhecidas(texto, permitidas) {
  const ok = new Set((permitidas || []).map((v) => (typeof v === "string" ? v.replace(/[{}]/g, "") : normalizaVariavel(v)?.nome)));
  return variaveisNoTexto(texto).filter((v) => !ok.has(v)).sort();
}

/** "{a}, {b}" */
export const listaVars = (nomes) => (nomes || []).map((v) => `{${v}}`).join(", ");

/** Como o SMS realmente sai: NFKD + só ASCII (igual ao sms_service do backend). */
export function smsAscii(texto) {
  return String(texto ?? "").normalize("NFKD").replace(/[^\x00-\x7F]/g, "");
}

/**
 * Tamanho do SMS: caracteres depois da conversão, partes de 160, se passou do limite (o envio
 * corta), quantos caracteres passam do limite (excedente, cortado) e se a conversão tirou algo
 * (acentos, emojis, símbolos). O sistema manda UM SMS só, cortado no 160º caractere: `partes`
 * existe só para conta; a tela fala em excedente cortado (textoTamanhoSms).
 */
export function smsInfo(texto) {
  const original = String(texto ?? "");
  const ascii = smsAscii(original);
  const caracteres = ascii.length;
  return {
    caracteres,
    partes: caracteres ? Math.ceil(caracteres / SMS_LIMITE) : 0,
    passou: caracteres > SMS_LIMITE,
    excedente: Math.max(0, caracteres - SMS_LIMITE),
    removidos: ascii !== original,
  };
}

/**
 * "120/160 caracteres" ou "170/160 caracteres · passa 10 do limite; o excedente é cortado no
 * envio" (o sms_service manda um SMS só, cortado em 160 — nunca em partes).
 */
export function textoTamanhoSms(caracteres) {
  const n = Math.max(0, Number(caracteres) || 0);
  const exc = n - SMS_LIMITE;
  const base = `${n}/${SMS_LIMITE} caracteres`;
  if (exc <= 0) return base;
  return `${base} · passa ${exc} ${exc === 1 ? "caractere" : "caracteres"} do limite; o excedente é cortado no envio`;
}

/**
 * Code points avulsos que o backend também recusa (services/mensagens.py, tem_emoji): ZWJ,
 * seletor de variação, keycap, símbolos CJK e os relógios/ampulhetas (o despertador U+23F0 era o
 * do aviso antigo de cashback vencendo).
 */
export const EMOJI_AVULSOS = [0x200D, 0xFE0F, 0x20E3, 0x3030, 0x303D, 0x3297, 0x3299, 0x23F0, 0x231A, 0x231B, 0x23F3];

/** Mesma faixa de emojis que o backend recusa no PUT (services/mensagens.py, tem_emoji). */
export function temEmoji(texto) {
  for (const ch of String(texto || "")) {
    const cp = ch.codePointAt(0);
    if ((cp >= 0x1F000 && cp <= 0x1FAFF) || (cp >= 0x2600 && cp <= 0x27BF) || (cp >= 0x2B00 && cp <= 0x2BFF)
        || (cp >= 0x1F1E6 && cp <= 0x1F1FF) || (cp >= 0xE0020 && cp <= 0xE007F)
        || EMOJI_AVULSOS.includes(cp)) {
      return true;
    }
  }
  return false;
}

/** Provedor de WhatsApp da API oficial (GET /master/whatsapp/oficial/status -> provedor_ativo). */
export const PROVEDOR_WHATSAPP_OFICIAL = "cdxzap_oficial";

/**
 * Qual WhatsApp está em uso agora, a partir do GET /master/whatsapp/oficial/status (o mesmo da
 * tela do WhatsApp). null = não deu para saber (a tela segue com o aviso genérico).
 * Devolve {id, rotulo, oficial, simulacao, textosValem}: com a API oficial os textos de WhatsApp
 * desta tela ficam SEM EFEITO (sai o template aprovado); com o provedor antigo são os que saem.
 */
export function provedorWhatsApp(status) {
  const id = String(status?.provedor_ativo ?? "").trim();
  if (!id) return null;
  const lista = Array.isArray(status?.provedores_disponiveis) ? status.provedores_disponiveis : [];
  const oficial = id === PROVEDOR_WHATSAPP_OFICIAL;
  const simulacao = id === "mock";
  const doServidor = lista.find((p) => p?.id === id)?.label;
  let rotulo;
  if (oficial) rotulo = "API oficial da Meta";
  else if (simulacao) rotulo = "Simulação (nenhuma mensagem sai)";
  else rotulo = doServidor ? `${doServidor}` : `Provedor antigo (${id})`;
  return { id, rotulo, oficial, simulacao, textosValem: !oficial && !simulacao };
}

/**
 * De onde vem o texto em uso no nível escolhido (unitId vazio = padrão do sistema):
 * "unidade" (texto próprio da unidade), "global" (padrão editado) ou "padrao" (texto original).
 */
export function origemTexto(item, unitId) {
  if (unitId && tem(item?.texto_unidade)) return "unidade";
  if (tem(item?.texto_global)) return "global";
  return "padrao";
}

/** Texto personalizado do PRÓPRIO nível escolhido (o que "Voltar ao padrão" apaga), ou "". */
export function textoDoNivel(item, unitId) {
  const t = unitId ? item?.texto_unidade : item?.texto_global;
  return tem(t) ? String(t) : "";
}

/** Texto que passa a valer se a personalização do nível for apagada. */
export function textoSemPersonalizacao(item, unitId) {
  if (unitId && tem(item?.texto_global)) return String(item.texto_global);
  return String(item?.texto_padrao ?? "");
}

/** O que passa a valer depois do "Voltar ao padrão" (para a confirmação). */
export function descricaoVoltarPadrao(item, unitId) {
  if (unitId) {
    return tem(item?.texto_global)
      ? "O padrão editado do sistema (vale para todas as unidades sem texto próprio)"
      : "O texto padrão do sistema";
  }
  return "O texto original do sistema (unidades com texto próprio continuam com o delas)";
}

/** Itens agrupados na ordem de CANAIS; canal desconhecido vai ao fim, com o próprio nome. */
export function agruparPorCanal(itens) {
  const lista = Array.isArray(itens) ? itens : [];
  const grupos = CANAIS.map((c) => ({ ...c, itens: lista.filter((i) => i?.canal === c.id) }));
  const conhecidos = new Set(CANAIS.map((c) => c.id));
  const outros = [...new Set(lista.map((i) => i?.canal).filter((c) => !conhecidos.has(c)))];
  outros.forEach((id) => grupos.push({ id, titulo: String(id || "Outros"), descricao: "", itens: lista.filter((i) => i?.canal === id) }));
  return grupos.filter((g) => g.itens.length);
}

/** Limite para salvar: limite_texto do backend; sem ele, max_caracteres fora do SMS; senão 1000. */
export function limiteTexto(item) {
  const lim = Number(item?.limite_texto) || 0;
  if (lim > 0) return lim;
  const max = Number(item?.max_caracteres) || 0;
  return item?.canal !== "sms" && max > 0 ? max : 1000;
}

/**
 * Validação de digitação, com as MESMAS recusas do PUT (o backend valida de novo). Devolve {erro, avisos}:
 * - erro (bloqueia salvar): passou do limite, emojis, variável desconhecida, obrigatória faltando;
 * - avisos (não bloqueiam): SMS que perde acentos/símbolos ou passa de 160 (o envio converte e corta).
 * Texto vazio não é erro: significa voltar ao padrão.
 */
export function validarTexto(texto, item) {
  const t = String(texto ?? "").replace(/\r\n?/g, "\n").trim();
  const avisos = [];
  if (!t) return { erro: null, avisos };
  const lim = limiteTexto(item);
  if (t.length > lim) return { erro: `O texto pode ter no máximo ${lim} caracteres.`, avisos };
  if (temEmoji(t)) return { erro: "O texto não pode ter emojis.", avisos };
  const validas = variaveisDoItem(item).map((v) => v.nome);
  const desconhecidas = variaveisDesconhecidas(t, validas);
  if (desconhecidas.length) {
    return {
      erro: `Variável desconhecida: ${listaVars(desconhecidas)}.${validas.length ? ` Use ${listaVars(validas)}.` : " Esta mensagem não aceita variáveis."}`,
      avisos,
    };
  }
  const presentes = new Set(variaveisNoTexto(t));
  const falta = gruposObrigatorios(item).find((g) => !g.some((v) => presentes.has(v)));
  if (falta) {
    return {
      erro: falta.length === 1 ? `O texto precisa ter a variável {${falta[0]}}.` : `O texto precisa ter uma destas variáveis: ${listaVars(falta)}.`,
      avisos,
    };
  }
  if (item?.canal === "sms") {
    const info = smsInfo(t);
    if (info.removidos) avisos.push("No SMS os acentos, cedilhas e símbolos são retirados.");
    if (info.passou) avisos.push(`O texto passa de ${SMS_LIMITE} caracteres: o SMS sai um só, cortado no ${SMS_LIMITE}º caractere.`);
  }
  return { erro: null, avisos };
}

/** Mensagem do erro da API (400 com a lista de variáveis pode vir como objeto). */
export function erroApiMensagens(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Erro inesperado";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || (typeof x === "string" ? x : JSON.stringify(x))).join(" ");
  if (typeof d === "object") {
    const msg = d.mensagem || d.message || d.erro || d.detail || "Texto recusado.";
    const lista = d.variaveis_desconhecidas || d.desconhecidas || d.variaveis;
    return Array.isArray(lista) && lista.length
      ? `${msg} ${listaVars(lista.map((v) => String(v).replace(/[{}]/g, "")))}`.trim()
      : String(msg);
  }
  return String(d);
}

/**
 * Verificação final da Fase 5: na visão "Padrão do sistema" o backend lista as unidades cujo texto
 * próprio é o que vale (unidades_texto_proprio). O padrão editado não muda nada nelas: a migração
 * deu texto próprio de agradecimento a cada unidade que já tinha config.
 */
export function unidadesComTextoProprio(item) {
  return Array.isArray(item?.unidades_texto_proprio) ? item.unidades_texto_proprio.filter((u) => u && u.unit_id) : [];
}

/** "2 unidades têm texto próprio e não mudam com este padrão: Alfa, Bravo." (null sem nenhuma). */
export function textoUnidadesProprias(lista) {
  const l = Array.isArray(lista) ? lista : [];
  const n = l.length;
  if (!n) return null;
  const nomes = l.map((u) => u.unit_nome || u.unit_id);
  const mostra = nomes.slice(0, 5).join(", ") + (n > 5 ? ` e mais ${n - 5}` : "");
  return n === 1
    ? `1 unidade tem texto próprio e não muda com este padrão: ${mostra}.`
    : `${n} unidades têm texto próprio e não mudam com este padrão: ${mostra}.`;
}
